import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { Grid, TextField, InputAdornment, Typography, Stack, IconButton } from '@mui/material'

import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import throttle from 'lodash.throttle'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import { useState, useEffect, useCallback } from 'react'
import { PatternFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'

import { acCleanBypassCustomer, acGetAllBypassCustomers, acGetBypassCustomer } from '../../store/actions'
import { formatCNPJ } from '../../utils'
import { PrimaryButton } from '../default'
import { AddBypassCustomerModal, RemoveBypassCustomerModal } from '../modals'

const BypassCustomersTable = ({ setSnackbarData }) => {

    const dispatch = useDispatch()

    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [openRemoveModal, setOpenRemoveModal] = useState(false)

    const [tableFilter, setTableFilter] = useState([])
    const [tableSorting, setTableSorting] = useState([])
    const [tablePagination, setTablePagination] = useState({
        pageSize: 10,
        pageIndex: 0
    })

    const [hasCustomers, setHasCustomers] = useState(false)

    const {
        rowCount, pagination, sorting, filter,

        allBypassCustomers,
        isLoadingAllBypassCustomers,
        hasErrorAllBypassCustomers,

        selectedBypassCustomer,
        isLoadingSelectedBypassCustomer,

        isCreatingBypassCustomer,
        isDeletingBypassCustomer
    } = useSelector(state => state.bypassCustomers)

    useEffect(() => {
        if (allBypassCustomers?.length > 0 && !hasCustomers)
            setHasCustomers(true)
    }, [allBypassCustomers])

    const handleInputSearch = useCallback(
        throttle(
            (filter, pagination, sorting) => {
                dispatch(acGetAllBypassCustomers({
                    filter: [...filter],
                    pagination: pagination,
                    sorting: sorting
                }))
            },
            1000,
            {
                leading: true,
                trailing: true
            }
        ),
        []
    )

    useEffect(() => {
        handleInputSearch(tableFilter, tablePagination, tableSorting)
        // eslint-disable-next-line
    }, [tablePagination, tableFilter, tableSorting])

    // Modal Funnctions
    const handleRemoveModalOpen = (row) => {
        dispatch(acGetBypassCustomer(row.original.bypass_customer_id))
            .then(() => {
                setOpenRemoveModal(!openRemoveModal)
            })
            .catch(error => console.error(error))
    }

    const handleCreateModalOpen = () => {
        setOpenCreateModal(!openCreateModal)
    }

    const handleCreateModalClose = () => {
        setOpenCreateModal(!openCreateModal)
        dispatch(acCleanBypassCustomer())
    }

    // Table Functions
    const renderRowActions = ({ row }) => {
        return (
            <IconButton
                onClick={() => handleRemoveModalOpen(row)}
                disabled={isLoadingSelectedBypassCustomer}
            >
                <DeleteIcon color={isLoadingSelectedBypassCustomer ? 'disabled' : 'primary'} />
            </IconButton>
        )
    }

    const renderDateColumn = (cell) => {
        const date = dayjs(cell.getValue())
        return date.locale('pt-br').format('DD/MM/YYYY HH:mm')
    }

    const columns = [
        {
            accessorKey: 'document',
            header: 'CNPJ',
            accessorFn: (raw) => formatCNPJ(raw.document)
        },
        {
            accessorKey: 'createdAt',
            header: 'Data de Adição',
            Cell: ({ cell }) => renderDateColumn(cell)
        }
    ]

    const table = useMaterialReactTable({

        columns,
        data: allBypassCustomers || [],
        localization: MRT_Localization_PT_BR,

        enableRowActions: true,
        positionActionsColumn: 'last',
        renderRowActions: renderRowActions,

        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,

        onColumnFiltersChange: setTableFilter,
        onPaginationChange: setTablePagination,
        onSortingChange: setTableSorting,

        enablePagination: true,
        enableColumnFilters: true,
        enableColumnActions: true,
        enableHiding: false,

        enableGlobalFilter: false,
        enableKeyboardShortcuts: false,
        enableTopToolbar: false,

        rowCount,

        initialState: {
            density: 'compact',
            sorting: [
                { id: 'createdAt', desc: true }
            ]
        },

        state: {
            isLoading: isLoadingAllBypassCustomers,
            isSaving: (isCreatingBypassCustomer || isDeletingBypassCustomer),
            showAlertBanner: hasErrorAllBypassCustomers,

            sorting,
            pagination,
            columnFilters: filter
        },

        muiTablePaperProps: () => ({
            elevation: 0
        }),
        mrtTheme: {
            baseBackgroundColor: '#FFFFFF'
        }
    })

    return (
        <>
            {!hasCustomers ? (
                <Stack gap={2} alignItems='center' width='100%'>
                    <InsertDriveFileOutlinedIcon color='primary' sx={{ fontSize: 40 }} />

                    <Typography variant='body2'>
                        Não há clientes registrados na lista de exceção. Você pode adicionar um novo CNPJ clicando no botão abaixo.
                    </Typography>

                    <PrimaryButton
                        label='Adicionar CNPJ'
                        Icon={AddIcon}
                        onClick={handleCreateModalOpen}
                    />
                </Stack>
            ) : (
                <Stack gap={2}>
                    <Grid container gap={2}>

                        <Grid item flexGrow={1}>
                            <PatternFormat
                                placeholder='Buscar CNPJ'
                                format='##.###.###/####-##'
                                mask='_'
                                customInput={TextField}
                                size='small'
                                fullWidth

                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}

                                value={tableFilter.document ?? ''}
                                valueIsNumericString
                                onValueChange={({ value }) => {
                                    setTableFilter([{ id: 'document', value: value || null }])
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs='auto'>
                            <PrimaryButton
                                label='Adicionar CNPJ'
                                Icon={AddIcon}
                                onClick={handleCreateModalOpen}
                            />
                        </Grid>
                        
                    </Grid>

                    <MaterialReactTable table={table} />

                    <RemoveBypassCustomerModal
                        open={openRemoveModal}
                        onClose={() => setOpenRemoveModal(!openRemoveModal)}
                        selectedBypassCustomer={selectedBypassCustomer}
                        setSnackbarData={setSnackbarData}
                    />
                </Stack>
            )}

            <AddBypassCustomerModal
                open={openCreateModal}
                onClose={handleCreateModalClose}
                setSnackbarData={setSnackbarData}
            />
        </>
    )
}

export default BypassCustomersTable
